import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import WarningBanner from '../../../../components/WarningBanner';
import { getConteggioVociRimappatura } from '../../../../utils/api/iot_rimappature_api';
import { leggiConteggioVociAperte } from './rimappatureUtils';

const useStyles = makeStyles((theme) => ({
  // Only what surrounds the block: the colours are WarningBanner's own.
  avviso: {
    margin: theme.spacing(2, 0),
  },
  // The «not known» state is a third state and does not dress up as an alarm:
  // there is nothing to go and review, there is a read that did not succeed.
  incerto: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    margin: theme.spacing(2, 0),
    textAlign: 'left',
  },
  bottone: {
    // `contrastText` of the yellow is dark: the button inherits the contrast of
    // the block instead of taking `primary`, which here would be brand-driven and
    // could disappear against the background.
    color: 'inherit',
    borderColor: 'currentColor',
    flexShrink: 0,
  },
}));

/**
 * The non-empty queue makes itself noticed where the manual gets published
 * (US 33, BSDEVPLAT-111): whoever publishes is whoever just produced the voci,
 * and the damage caused — a fridge that stops being watched — raises no alarm of
 * its own.
 *
 * One read, on mount and on every new `ricaricaToken`. **No polling**: the remap
 * happens inside the publication transaction (ADR-0008), so by the time the
 * publication POST returns the trace is already there, and every writer that can
 * change the count afterwards bumps the token — the publication itself, and each
 * review done from the «Assegnazioni IoT» tab. bs-safe-app can close a voce too,
 * by reassigning the probe from the shop floor, and that one this screen does not
 * see: a poll would cost one request per open screen per interval, against the
 * declared target of ~1M probes (specs/infra-media-mobile-alerting §1), to catch
 * a write that leaves the count too *high* — never too low — until the next read.
 */
const AvvisoCodaRevisione = ({ esercizioId, onApriCoda, ricaricaToken }) => {
  const classes = useStyles();
  // `undefined` = not read yet, `null` = read and not understood, number = read.
  // The three states are kept distinct on purpose: inferring «queue empty» from
  // the absence of a number is the failure mode this banner exists to close.
  // `illeggibile` is derived and not a second state variable: two copies of one
  // fact can disagree, and the whole point here is that they must not.
  const [aperte, setAperte] = useState(undefined);
  const illeggibile = aperte === null;

  useEffect(() => {
    let attivo = true;

    if (esercizioId == null || esercizioId === '') {
      // No subject, so no count to vouch for: a previously read one would keep
      // rendering for an esercizio that is no longer the subject.
      setAperte(undefined);
      return undefined;
    }

    // Back to «not read yet» for the whole in-flight read: right after a
    // publication the old value is the *pre*-publication count, and that is the
    // one moment the operator is looking at this.
    setAperte(undefined);
    getConteggioVociRimappatura(esercizioId)
      .then((risposta) => {
        if (!attivo) return;
        setAperte(leggiConteggioVociAperte(risposta));
      })
      .catch(() => {
        if (!attivo) return;
        setAperte(null);
      });

    return () => {
      attivo = false;
    };
  }, [esercizioId, ricaricaToken]);

  if (illeggibile) {
    return (
      <Typography
        variant="body2"
        component="p"
        className={classes.incerto}
        data-testid="avviso-coda-revisione-incerto"
      >
        Non è stato possibile leggere la coda di revisione delle assegnazioni
        IoT: quante sonde siano rimaste senza controllo non si sa. Controllala
        nella scheda «Assegnazioni IoT».
      </Typography>
    );
  }

  if (!aperte) {
    return null;
  }

  const singola = aperte === 1;

  return (
    <WarningBanner
      className={classes.avviso}
      testId="avviso-coda-revisione"
      action={
        onApriCoda && (
          <Button
            size="small"
            variant="outlined"
            className={classes.bottone}
            onClick={onApriCoda}
          >
            Vai alla coda
          </Button>
        )
      }
    >
      {singola
        ? '1 assegnazione di sonda è rimasta senza controllo dopo una pubblicazione:'
        : `${aperte} assegnazioni di sonde sono rimaste senza controllo dopo una pubblicazione:`}{' '}
      quelle attrezzature non sono più sorvegliate e nessun allarme lo dirà.
      Vanno riassegnate o archiviate con una ragione.
    </WarningBanner>
  );
};

AvvisoCodaRevisione.propTypes = {
  esercizioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onApriCoda: PropTypes.func,
  ricaricaToken: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default AvvisoCodaRevisione;
